import React from "react";
import ChevronDown from "../../../../svg/chevron-down";

/**
 * Chevron toggle cell for opt-in expandable custom-table rows.
 *
 * Reads `value.isExpanded` from row data and emits `onRowExpandChange` so the
 * consumer can update `row.expandableRowIcon.isExpanded` in table state.
 *
 * @property value - Cell payload with expansion state and optional border styling
 * @property onRowExpandChange - Callback fired when the user toggles expansion
 */
interface ExpandableRowIconCellProps {
  value: {
    borderColor?: string;
    borderWidth?: number;
    id?: string;
    isExpanded?: boolean;
  };
  onRowExpandChange?: (expanded: boolean, id: string) => void;
}

/**
 * Renders a chevron toggle for expandable table rows.
 *
 * @param props - See {@link ExpandableRowIconCellProps}
 * @returns The chevron toggle element
 */
const ExpandableRowIconCell = (props: ExpandableRowIconCellProps) => {
  const expanded = props.value?.isExpanded ?? false;

  /**
   * Toggles row expansion and stops the click from bubbling to the row handler.
   *
   * @param e - Mouse event from the chevron container
   */
  const handleToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const newExpanded = !expanded;
    props.onRowExpandChange?.(newExpanded, props.value?.id ?? "");
  };

  return (
    <div
      className="flex h-full w-full items-center"
      style={{
        borderColor: `${props.value.borderColor}`,
        borderLeftWidth: props.value.borderWidth,
        transition: "border-color 0.3s ease",
      }}
      onClick={handleToggle}
    >
      <div className="ml-3">
        <ChevronDown
          rotate={!expanded ? 0 : -180}
          height={18}
          width={18}
          strokeWidth={1.55}
        />
      </div>
    </div>
  );
};

export default ExpandableRowIconCell;
